import { Location } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { logger } from '@app/core/helpers/logger';
import { setCurrentItem } from '@app/store/actions/content.actions';
import { CLibraryFilterData } from '@app/store/models';
import { vimeoEmbedUrl, youtubeEmbedUrl } from '@core/helpers/video';
import {
  AccountDetailsType,
  AccountService,
  AuthService,
  BillingV2Service,
  CommentService,
  ConfigService,
  ContextService,
  FeaturesService,
  FilesService,
  fullUserServiceDetails,
  LanguageService,
  MetaService,
  PlatformService,
  SchoolsService,
  UserServicesService,
} from '@core/services';
import { ISortedContentBlock } from '@core/services/interfaces';
import { ChangableComponent } from '@models/changable.component';
import { LibraryRouter } from '@models/library.router';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { ApplicationDialogsService } from '@shared/application-dialogs';
import { ErrorNotificationService } from '@shared/error-notification/error-notification.service';
import { getLibraryRouteData } from '@store/reducers/library.reducer';
import {
  CSchool,
  EUserServiceType,
  FeatureEnum,
  IAccountStats,
  IContentFilterBlock,
  IFindMeetingReviewFilter,
  IFindUserServicesFilter,
  ImageSizeEnum,
  IOrder,
  IPagination,
  Language,
  MeetingTypeEnum,
  ProfileMediaModeEnum,
  ReviewListItem,
  SubjectCategoryEnum,
  User,
  UserProfile,
  UserService,
  UserServiceCategory,
  UserStatusEnum,
} from 'lingo2-models';
import { IFindAccountsFilter } from 'lingo2-models/dist/account/interfaces/find-accounts-filter';
import { Subject as BaseSubject } from 'lingo2-models/dist/content/subject';
import { FindUserServiceStrategyEnum } from 'lingo2-models/dist/content/user-service/find-user-services-filter';
import { DeviceDetectorService } from 'ngx-device-detector';
import { OnUiCover } from 'onclass-ui';
import { fromEvent, map, mergeMap, Observable, takeUntil } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

const daysOfWeek = ['su', 'mo', 'tu', 'we', 'th', 'fr', 'sa', 'su'];

interface IUserServiceCardComponentState {
  copied: boolean;
  cut: boolean;
  subscribed: boolean;
}

type ReviewsBlockType = ISortedContentBlock<ReviewListItem, IFindMeetingReviewFilter, IOrder<ReviewListItem>>;

@Component({
  selector: 'app-user-service-card-full',
  templateUrl: './user-service-card-full.component.html',
  styleUrls: [
    './user-service-card-full.component.scss',
    '../../library/panes/filter-content/filter-content.component.scss',
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserServiceCardFullComponent extends ChangableComponent implements OnInit, OnDestroy {
  @ViewChild('navPosition') navPosition: ElementRef;
  @Input() public set userService(userService: UserService) {
    const oldUserServiceId = this._userService?.id || null;
    const newUserServiceId = userService?.id || null;
    this._userService = userService instanceof UserService ? userService : new UserService(userService);
    if (this._userService && oldUserServiceId !== newUserServiceId) {
      this.showSkeleton = true;
      this.store.dispatch(setCurrentItem({ item: this._userService }));
      this.state.cut = (this.userService.description || '').length > this.cutMaxLength;
      if (this._userService?.school_id) {
        this.loadProfiles();
      }
      this.setupCard();
      this.loadOtherUserServices();
      this.setFilterForLatestMeetingReviews();
      this.setFilterForMeetingReviews();
      this.loadMeetingReviews(this.latestMeetingReviews, true);
      this.loadMeetingReviews(this.meetingReviews);
    }
  }

  public get userService(): UserService {
    return this._userService;
  }

  @Input() modal: boolean;
  @Output() inViewportChange = new EventEmitter<boolean>();
  @Output() public isCloseAllModals = new EventEmitter<boolean>();
  public me: User;
  public isGuest: boolean;
  public accountRoute: string[];
  public fullTitle: string;
  public cutMaxLength = 214;
  public state: IUserServiceCardComponentState = {
    copied: false,
    cut: false,
    subscribed: false,
  };
  public authorFullName: string;
  public subject: string;
  public isNativeLanguage: boolean;
  public title: string;
  public excerpt: string;
  public description: string;
  public level: string;
  public cover: OnUiCover;
  public authorAvatar: OnUiCover;
  public school: CSchool;
  public coverImageUrl: string;
  public userServiceUrl: string;
  public category: string;
  public discount: number;
  public duration: number;
  public price_tier: number;
  public stats: Partial<IAccountStats>;
  public debug$ = this.contextService.debug$;
  public isMediaPrepared = false;
  public isMediaPreparedMain = false;
  public safeMediaUrl: SafeResourceUrl;
  public safeMediaUrlMain: SafeResourceUrl;
  public otherUserServices: UserService[];
  public checkoutModalOpen = false;
  public userServiceCardFullInModal = false;
  public mainTabs: IContentFilterBlock[] = [];
  public activeTab = '';
  public showSkeleton: boolean;
  public loaded: boolean;
  public otherUserServicesTabs: UserServiceCategory[];
  public selectOtherUserServicesFilter: UserServiceCategory;
  public isVideoFromYoutube = false;
  public stuckState: boolean;
  public topFromHeader: number;

  public meetingAuthor: User;

  public latestMeetingReviews: ReviewsBlockType = {
    items: [],
    loading: false,
    loaded: false,
    filter: {},
    pagination: {
      page: 1,
      pageSize: 10,
      total: 0,
      totalPages: 1,
    },
    order: [['updatedAt', 'DESC']],
  };
  public meetingReviews: ReviewsBlockType = {
    items: [],
    loading: false,
    loaded: false,
    filter: {},
    pagination: {
      page: 1,
      pageSize: 10,
      total: 0,
      totalPages: 1,
    },
    order: [['updatedAt', 'DESC']],
  };
  public language: Language;
  public subjectRoute: string;
  public userServiceRoute: string;
  public metaImageUrl: string;
  public contentDate: Date;
  public age_from: number;
  public age_to: number;
  public participants_limit: number;
  public meetings_limit: number;
  public weekdays: number[];
  public subjectIsNative: boolean;
  public requirements: string[];
  public skills: string[];
  public eUserServiceType = EUserServiceType;
  public type: EUserServiceType;
  public openedUserService: UserService;
  public surfaceBlockDisplay: boolean;
  private originalUrl: string;

  private _userService: UserService;
  private _profile: UserProfile;
  private _originalMeta: any;
  private _libraryFilterData: CLibraryFilterData;
  private schoolTeachers: Array<Partial<User>>;

  constructor(
    public errorNotificationService: ErrorNotificationService,
    public deviceService: DeviceDetectorService,
    private features: FeaturesService,
    private accountService: AccountService,
    private authService: AuthService,
    private billing2Service: BillingV2Service,
    private filesService: FilesService,
    private userServicesService: UserServicesService,
    private configService: ConfigService,
    private contextService: ContextService,
    private commentService: CommentService,
    private schoolService: SchoolsService,
    private sanitizer: DomSanitizer,
    protected dialogsService: ApplicationDialogsService,
    private languageService: LanguageService,
    private location: Location,
    private meta: MetaService,
    private store: Store,
    private translate: TranslateService,
    protected cdr: ChangeDetectorRef,
    protected platform: PlatformService,
  ) {
    super(cdr, platform);
  }

  ngOnInit() {
    this.contextService.me$.pipe(takeUntil(this.destroyed$)).subscribe({
      next: (me) => {
        this.me = me;
        this.isGuest = me && me.status === UserStatusEnum.guest;
        this.detectChanges();
      },
      error: (error) => {
        this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
      },
    });

    this.languageService.language$.pipe(takeUntil(this.destroyed$)).subscribe({
      next: (language) => {
        this.language = language;
      },
      error: (error) => {
        this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
      },
    });

    this.store
      .select(getLibraryRouteData)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (libraryFilterData) => {
          this._libraryFilterData = libraryFilterData;
          this.subjectRoute = this.getSubjectRoute();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });

    if (this.modal) {
      this.setupMeta();
    }

    if (this.deviceService.isMobile() && this.isBrowser) {
      this.setTimeout(() => {
        this.topFromHeader = this.modal ? 0 : document.getElementsByClassName('header')[0].clientHeight;
        const stickyElm = this.modal ? document.querySelector('app-mobile-modal-body') : window;
        fromEvent(stickyElm, 'scroll')
          .pipe(debounceTime(10), takeUntil(this.destroyed$))
          .subscribe({
            next: () => {
              this.stuckState = this.navPosition?.nativeElement?.getBoundingClientRect()?.top == this.topFromHeader;
              this.detectChanges();
            },
            error: (error) => {
              this.errorNotificationService.captureError(error, 'OTHER-PROBLEM');
              this.stuckState = false;
              this.detectChanges();
            },
          });
      }, 100);
    }
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    if (this.modal) {
      this.resetMeta();
    }
  }
  protected setupCard() {
    if (this.userService.school_id) {
      this.schoolService
        .getSchoolByIdOrSlug(this.userService?.school_id, undefined, 'LOAD_TIME')
        .pipe(takeUntil(this.destroyed$))
        .subscribe({
          next: (school) => {
            this.school = school;
            this.accountRoute = SchoolsService.schoolRoute(school, false);
            this.authorFullName = school.title;
            this.authorAvatar = {
              type: 'avatar',
              img: FilesService.getFileUrlBySize(school.cover_id, ImageSizeEnum.sm),
              title: this.authorFullName,
              link: this.accountRoute.join('/'),
              form: 'none',
            };
            this.detectChanges();
          },
          error: (error) => {
            this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
          },
        });
    } else {
      this.accountService
        .getUserById(this.userService.author_id, 'LOAD_TIME')
        .pipe(takeUntil(this.destroyed$))
        .subscribe({
          next: (user) => {
            this.meetingAuthor = user;
            this.stats = user.stats;
            this.accountRoute = AccountService.accountRoute(this.meetingAuthor);
            this.authorFullName = AccountService.getUserName(this.meetingAuthor);
            this.authorAvatar = {
              type: 'avatar',
              img: FilesService.getFileUrlBySize(this.meetingAuthor.userpic_id, ImageSizeEnum.sm),
              title: this.authorFullName,
              link: this.accountRoute.join('/'),
              form: 'circle',
            };
            this.contextService.updateUser(user);
            this.detectChanges();
          },
          error: (error) => {
            this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
          },
        });
    }

    this.coverImageUrl =
      FilesService.getFileBySize(this.userService?.cover, [ImageSizeEnum.wide, ImageSizeEnum.md])?.url ||
      FilesService.getFileUrlBySize(this.userService?.cover_id, ImageSizeEnum.wide);
    this.userServiceUrl = LibraryRouter.userServiceRouteUniversal(this.userService);
    this.subject = this.userService.subject ? this.userService.subject?.title : null;
    this.isNativeLanguage = this.userService?.subject?.category_id === SubjectCategoryEnum.native_language;
    // TODO this.level = this.userService.level ? this.userService.level.title : 'subject-levels.any-level';
    this.type = this.userService?.type;
    this.title = this.userService?.title;
    this.excerpt = this.userService?.excerpt;
    this.description = this.userService?.description;
    this.fullTitle = this.userServicesService.userServiceFullTitle(this.userService);
    this.category = this.userService?.category?.title;
    this.discount = this.userService?.discount;
    this.duration = this.userService?.duration;
    this.price_tier = this.userService?.price_tier;
    this.userServiceRoute = LibraryRouter.userServiceRouteUniversal(this.userService);
    this.contentDate = new Date();
    this.cover = {
      type: 'cover',
      img: this.coverImageUrl,
      title: this.title,
      link: this.userServiceUrl,
      form: 'base',
      aspect: '16/9',
    };
    this.age_from = this.userService?.options?.age_from;
    this.age_to = this.userService?.options?.age_to;
    this.meetings_limit = this.userService?.options?.meetings_limit;
    this.weekdays = this.userService?.options?.weekdays;
    this.participants_limit = this.userService?.participants_limit;
    this.subjectIsNative = this.userService?.subject?.category_id === SubjectCategoryEnum.native_language;
    this.requirements = this.userService?.options?.requirements;
    this.skills = this.userService?.options?.skills;
    if (this._userService.media_url) {
      this.safeMediaUrlMain = this.sanitizer.bypassSecurityTrustResourceUrl(
        FilesService.getFileUrlBySize(this._userService.media_url),
      );
    }
    this.prepareMetaImage();
  }

  public formatAge(age: number) {
    return age > 65 ? '65+' : age;
  }

  public navigate(tab: IContentFilterBlock) {
    this.loaded = false;

    this.setTimeout(() => {
      this.loaded = true;
      this.location.replaceState(this.userServiceUrl + '/' + tab.code);
      this.detectChanges();
    }, 500);
  }

  public isActiveRoute(route: string): boolean {
    const url = this.location.path();
    const link = url.split('/').filter((item) => item.length);
    const last = decodeURIComponent(link.reverse()[0]);
    this.activeTab = this.userServiceUrl === decodeURIComponent(url) ? '' : last;
    return last === route || last === 'main' || (this.userServiceUrl === decodeURIComponent(url) && route === 'main');
  }

  public priceWithoutDiscount(amount: number): number {
    return +amount / ((100 - +this.discount) / 100);
  }

  public isRegularClass(): boolean {
    return this.userService.type === EUserServiceType.regular;
  }

  public getFormattedAmount(): Observable<string> {
    return this.billing2Service.formatAmount$(this.price_tier);
  }

  public onInViewportChange(e) {
    this.inViewportChange.emit(e.visible);
  }

  public onSelectOtherUserServicesFilter(category: UserServiceCategory) {
    this.selectOtherUserServicesFilter = category;
    this.detectChanges();
  }

  public filterUserServices(userServices: UserService[]): UserService[] {
    if (!this.selectOtherUserServicesFilter || this.selectOtherUserServicesFilter?.id === -1) {
      return userServices;
    }
    return userServices.filter((_us) => _us.category?.id === this.selectOtherUserServicesFilter.id);
  }

  public openCheckoutModal() {
    const onAuthComplete = () => {
      this.checkoutModalOpen = true;
      this.dialogsService.openUserServiceCheckoutWizard({
        userServiceId: this.userService.id,
      });
      this.detectChanges();
    };

    if (this.isGuest) {
      this.authService.showAuthModal(onAuthComplete);
    } else {
      onAuthComplete();
    }
  }

  public closeCheckoutModal() {
    this.checkoutModalOpen = false;
    this.detectChanges();
  }

  public get isSingle(): boolean {
    return this.participants_limit === 1;
  }

  public weekdaysToString(): string {
    return this.weekdays?.map((_d) => this.translate.instant(`days-of-week.${daysOfWeek[_d]}`)).join(', ');
  }

  public get labelPeriod(): string {
    switch (this._userService.type) {
      case EUserServiceType.single:
        return 'library.user-service-card.label-period.single';
      case EUserServiceType.regular:
        return 'library.user-service-card.label-period.regular';
      case EUserServiceType.mini:
        return 'library.user-service-card.label-period.mini';
      case EUserServiceType.course:
        return 'library.user-service-card.label-period.course';
    }
  }

  public typeLabel(type: EUserServiceType): string {
    switch (type) {
      case EUserServiceType.single:
        return 'library.user-service-card.type.single';
      case EUserServiceType.regular:
        return 'library.user-service-card.type.regular';
      case EUserServiceType.mini:
        return 'library.user-service-card.type.mini';
      case EUserServiceType.course:
        return 'library.user-service-card.type.course';
      default:
        return '';
    }
  }

  public onToggleUserServiceInModal(userService: UserService = null) {
    this.openedUserService = userService;
    this.userServiceCardFullInModal = !this.userServiceCardFullInModal;

    if (!this.userServiceCardFullInModal) {
      this.isCloseAllModals.emit(true);
    }

    this.setAndResetUrl();
    this.detectChanges();
  }

  public onIinViewportChange(e) {
    this.surfaceBlockDisplay = e;
    this.detectChanges();
  }

  public get isSchool(): boolean {
    return !!this._userService?.school_id;
  }

  private activeVideoTab: Partial<User>;

  public teachersTabs(): Array<Partial<User>> {
    return this.schoolTeachers;
  }

  public isActiveVideo(tab: Partial<User>): boolean {
    return this.activeVideoTab?.id === tab.id;
  }

  public onActiveVideo(tab: Partial<User>) {
    this.activeVideoTab = tab;
    this.prepareMedia();
    this.detectChanges();
  }

  public videoTabLabel(tab: Partial<User>): string {
    return `${tab?.first_name?.substring(0, 1)}. ${tab?.last_name}`;
  }

  public printDebugInfo() {
    logger.log({
      service: this.userService,
    });
  }

  private prepareMedia() {
    const selectedSubjectId = this._userService.subject_id;
    const teachingSubject = this.activeVideoTab?.profile?.teaching_subjects.find(
      (_ts) => _ts.subject_id === selectedSubjectId,
    );
    if (!teachingSubject || (!teachingSubject.video_url && !teachingSubject.video_id)) {
      this.safeMediaUrl = null;
      this.isMediaPrepared = false;
      return;
    }

    if (teachingSubject.video_id && teachingSubject.video_mode === ProfileMediaModeEnum.use_media_id) {
      this.filesService
        .getFileById(teachingSubject.video_id)
        .pipe(takeUntil(this.destroyed$))
        .subscribe({
          next: (file) => {
            this.safeMediaUrl = this.sanitizer.bypassSecurityTrustResourceUrl(file.storage_file_url);
            this.isMediaPrepared = true;
            this.detectChanges();
          },
          error: (error) => {
            this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
            this.safeMediaUrl = null;
            this.isMediaPrepared = true;
          },
        });
      this.isVideoFromYoutube = false;
    } else if (teachingSubject.video_url && teachingSubject.video_mode === ProfileMediaModeEnum.use_media_url) {
      this.safeMediaUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
        youtubeEmbedUrl(teachingSubject.video_url) || vimeoEmbedUrl(teachingSubject.video_url),
      );
      this.isMediaPrepared = true;
      this.isVideoFromYoutube = true;
    } else {
      this.safeMediaUrl = null;
      this.isMediaPrepared = false;
    }
  }

  private loadProfiles() {
    const accountsFilter: Partial<IFindAccountsFilter> = {
      id: this._userService.teacher_ids,
    };
    const pagination = {
      page: 1,
      pageSize: 100,
    };
    const details: AccountDetailsType[] = ['id', 'first_name', 'last_name', 'profile'];

    this.accountService
      .findAccounts(accountsFilter, pagination, details)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (users) => {
          this.schoolTeachers = users.results;
          this.onActiveVideo(this.teachersTabs()[0]);
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
  }

  private setAndResetUrl() {
    const url = LibraryRouter.userServiceRouteUniversal(this.openedUserService);
    const currentUrl = this.location.path(false);
    if (this.userServiceCardFullInModal) {
      if (currentUrl !== url) {
        if (!this.originalUrl) {
          this.originalUrl = currentUrl;
        }
        this.location.replaceState(url);
      }
    } else {
      if (this.originalUrl) {
        this.location.replaceState(this.originalUrl);
        this.originalUrl = null;
      }
    }
  }

  private getSubjectRoute(): string {
    const subjectRouter = new LibraryRouter();
    subjectRouter.setBaseRoute(this._libraryFilterData?.baseRoute);
    subjectRouter.setSubject(this.userService?.subject?.code);
    return subjectRouter.getLink().path;
  }

  public get profile(): UserProfile {
    return this._profile;
  }

  private prepareMetaImage() {
    this.metaImageUrl =
      FilesService.getFileBySize(this.userService.cover, [ImageSizeEnum.lg, ImageSizeEnum.md])?.url ||
      FilesService.getFileUrlBySize(this.userService.cover_id, ImageSizeEnum.lg);
  }

  private setFilterForLatestMeetingReviews() {
    this.latestMeetingReviews.filter.author_id = this.userService?.author_id;
  }

  private setFilterForMeetingReviews() {
    this.meetingReviews.filter.author_id = this.userService?.author_id;
  }

  private loadMeetingReviews(meetingReviewsBlockType: ReviewsBlockType, commented?: boolean) {
    this.commentService
      .findReviews(
        'user-service',
        this.userService.id,
        meetingReviewsBlockType.pagination,
        meetingReviewsBlockType.order,
        commented,
      )
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (response) => {
          meetingReviewsBlockType.items = response.results;
          this.loaded = true;
          this.setMainTabs();
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
          this.loaded = true;
          this.setMainTabs();
        },
      });
  }

  private loadOtherUserServices() {
    const pagination: IPagination = {
      page: 1,
      pageSize: 10,
      total: 0,
      totalPages: 0,
    };

    const filter: Partial<IFindUserServicesFilter> = {
      use: FindUserServiceStrategyEnum.library,
      author_id: this.userService?.author_id,
      exclude_id: [this.userService?.id],
    };

    this.userServicesService
      .getServices(filter, pagination, fullUserServiceDetails)
      .pipe(
        map((us) => {
          this.otherUserServices = us.results;
          this.setMainTabs();
          return us;
        }),
        mergeMap(() => this.configService.getUserServiceCategories(this.language?.code)),
        takeUntil(this.destroyed$),
      )
      .subscribe({
        next: (userServiceCategory) => {
          const tabs = userServiceCategory.filter(
            (_category) => !!this.otherUserServices.find((_us) => +_us.category?.id === +_category.id),
          );
          this.otherUserServicesTabs = [
            { id: -1, code: 'all', title: 'user-service-card-full.all', types: [MeetingTypeEnum.not_defined] },
            ...tabs,
          ];
          this.selectOtherUserServicesFilter = this.otherUserServicesTabs[0];
          this.showSkeleton = false;
          this.loaded = true;
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
          this.showSkeleton = false;
          this.loaded = true;
        },
      });
  }

  private setMainTabs() {
    this.mainTabs = [];
    this.mainTabs.push({
      order: 0,
      id: 0,
      title: 'user-service-card-full.tabs.main',
      code: '',
      type: 'subject',
      item: new BaseSubject(),
      total_count: 0,
    });

    this.mainTabs.push({
      order: 1,
      id: 1,
      title: 'user-profile.tab-about-tutor',
      code: 'about',
      type: 'subject',
      item: new BaseSubject(),
      total_count: 0,
    });

    if (!!this.otherUserServices?.length) {
      this.mainTabs.push({
        order: 2,
        id: 2,
        title: 'user-service-card-full.tabs.other-lessons',
        code: 'other',
        type: 'subject',
        item: new BaseSubject(),
        total_count: 0,
      });
    }

    if (!!this.meetingReviews?.items?.length) {
      this.mainTabs.push({
        order: 3,
        id: 3,
        title: 'user-service-card-full.tabs.reviews',
        code: 'reviews',
        type: 'subject',
        item: new BaseSubject(),
        total_count: 0,
      });
    }
  }

  private setupMeta() {
    this._originalMeta = {
      title: this.meta.getTitle(),
    };

    const title = this.userServicesService.userServiceFullTitle(this.userService);
    this.meta.setTitle([title, 'meetings.meta-title']);
  }

  private resetMeta() {
    this.meta.setTitle([this._originalMeta.title]);
    this._originalMeta = null;
  }

  public get isAuthor(): boolean {
    return this.me && this.userService && this.me.id === this.userService.author_id;
  }

  public get isUnderModerationOrDecline(): boolean {
    return this._userService.is.on_moderation || this._userService.is.declined;
  }

  public trackByFn(index) {
    return index;
  }
}
